
import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { PACKAGES, COUNTRIES } from '../data';
import { PackageCard } from './Home';

// --- Shared Logic Components ---

const DropdownWrapper: React.FC<{ 
  isOpen: boolean; 
  onClose: () => void; 
  children: React.ReactNode; 
  className?: string;
  position?: 'top' | 'bottom';
}> = ({ isOpen, onClose, children, className, position = 'bottom' }) => {
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        onClose();
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const positionClasses = position === 'bottom' 
    ? 'top-full mt-3' 
    : 'bottom-full mb-3';

  return (
    <div 
      ref={wrapperRef}
      className={`fixed inset-x-4 top-20 md:absolute md:inset-x-auto md:left-0 z-[500] bg-white rounded-lg shadow-[0_30px_60px_-15px_rgba(0,0,0,0.3)] border border-gray-100 animate-fade-in md:w-auto w-[calc(100vw-32px)] ${positionClasses} ${className}`}
    >
      {children}
    </div>
  );
};

const CalendarPicker: React.FC<{ onClose: () => void; onSelect: (val: string) => void }> = ({ onClose, onSelect }) => {
  const [activeTab, setActiveTab] = useState('period');
  const [viewDate, setViewDate] = useState(new Date(2026, 5, 1)); 
  const [startDate, setStartDate] = useState<Date | null>(new Date(2026, 5, 10)); 
  const [endDate, setEndDate] = useState<Date | null>(new Date(2026, 5, 17));    
  
  const days = ['PR', 'OT', 'TR', 'CE', 'PK', 'SE', 'SV'];
  const monthNames = [
    'Janvāris', 'Februāris', 'Marts', 'Aprīlis', 'Maijs', 'Jūnijs',
    'Jūlijs', 'Augusts', 'Septembris', 'Oktobris', 'Novembris', 'Decembris'
  ];

  const getDaysInMonth = (year: number, month: number) => new Date(year, month + 1, 0).getDate();
  const getFirstDayOfMonth = (year: number, month: number) => {
    const day = new Date(year, month, 1).getDay();
    return day === 0 ? 6 : day - 1; 
  };

  const generateMonthData = (year: number, month: number) => {
    const totalDays = getDaysInMonth(year, month);
    const startOffset = getFirstDayOfMonth(year, month);
    const prevMonthDays = getDaysInMonth(year, month - 1);
    const grid = [];

    for (let i = startOffset - 1; i >= 0; i--) {
      grid.push({ date: new Date(year, month - 1, prevMonthDays - i), current: false });
    }
    for (let i = 1; i <= totalDays; i++) {
      grid.push({ date: new Date(year, month, i), current: true });
    }
    const remaining = 42 - grid.length;
    for (let i = 1; i <= remaining; i++) {
      grid.push({ date: new Date(year, month + 1, i), current: false });
    }
    return grid;
  };

  const handleDateClick = (date: Date) => {
    if (!startDate || (startDate && endDate)) {
      setStartDate(date);
      setEndDate(null);
    } else {
      if (date < startDate) {
        setStartDate(date);
        setEndDate(null);
      } else {
        setEndDate(date);
      }
    }
  };

  const isInRange = (date: Date) => {
    return startDate && endDate && date > startDate && date < endDate;
  };

  const changeMonth = (offset: number) => {
    const newDate = new Date(viewDate.getFullYear(), viewDate.getMonth() + offset, 1);
    setViewDate(newDate);
  };

  const renderMonth = (date: Date, showPrevBtn = false, windowSize768 = false) => {
    const monthData = generateMonthData(date.getFullYear(), date.getMonth());
    return (
      <div className="flex-1 w-full min-w-[300px]">
        <div className="flex items-center justify-center mb-6 md:mb-8 relative">
          {showPrevBtn && (
            <button 
              type="button" 
              onClick={() => changeMonth(-1)}
              className="absolute left-0 w-8 h-8 md:w-10 md:h-10 rounded-lg border border-gray-100 flex items-center justify-center text-gray-400 hover:bg-gray-50 transition-colors"
            >
              <i className="fas fa-chevron-left text-[10px] md:text-[12px]"></i>
            </button>
          )}
          <h4 className="text-base md:text-lg font-black text-[#05073C]">
            {monthNames[date.getMonth()]} {date.getFullYear()}
          </h4>
          {windowSize768 && (
            <button 
              type="button" 
              onClick={() => changeMonth(1)}
              className="absolute right-0 w-8 h-8 md:w-10 md:h-10 rounded-lg border border-gray-100 flex items-center justify-center text-gray-400 hover:bg-gray-50 transition-colors"
            >
              <i className="fas fa-chevron-right text-[10px] md:text-[12px]"></i>
            </button>
          )}
        </div>
        <div className="grid grid-cols-7 mb-4">
          {days.map(d => <div key={d} className="text-[10px] md:text-[11px] font-black text-brand-deep/30 text-center py-2">{d}</div>)}
        </div>
        <div className="grid grid-cols-7 gap-y-1">
          {monthData.map((d, i) => {
            const start = startDate && d.date.getTime() === startDate.getTime();
            const end = endDate && d.date.getTime() === endDate.getTime();
            const range = isInRange(d.date);
            
            return (
              <div 
                key={i} 
                className={`relative text-[12px] md:text-[13px] font-bold py-3 text-center cursor-pointer transition-all rounded-lg
                  ${!d.current ? 'text-brand-deep/10' : 'text-brand-deep hover:bg-orange-50 hover:text-brand-accent'}
                  ${start ? 'bg-[#05073C] text-white z-10 !text-white' : ''}
                  ${end ? 'bg-[#05073C] text-white z-10 !text-white' : ''}
                  ${range ? 'bg-orange-50 text-brand-accent' : ''}
                `}
                onClick={() => d.current && handleDateClick(d.date)}
              >
                {d.date.getDate()}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const secondMonthDate = new Date(viewDate.getFullYear(), viewDate.getMonth() + 1, 1);

  return (
    <div className="w-full md:w-[780px] overflow-x-hidden overflow-y-auto max-h-[85vh] md:max-h-none">
      <div className="flex border-b border-gray-100">
        <button 
          type="button"
          onClick={() => setActiveTab('period')}
          className={`flex-1 py-4 md:py-6 text-[11px] md:text-[13px] font-black uppercase tracking-wider transition-all ${activeTab === 'period' ? 'text-brand-accent border-b-2 border-brand-accent' : 'text-brand-deep/40'}`}
        >
          Periods
        </button>
        <button 
          type="button"
          onClick={() => setActiveTab('exact')}
          className={`flex-1 py-4 md:py-6 text-[11px] md:text-[13px] font-black uppercase tracking-wider transition-all ${activeTab === 'exact' ? 'text-brand-accent border-b-2 border-brand-accent' : 'text-brand-deep/40'}`}
        >
          Datumi
        </button>
      </div>

      <div className="p-4 md:p-10 flex flex-col md:flex-row gap-6 md:gap-16 relative">
        {renderMonth(viewDate, true, window.innerWidth < 768)}
        <div className="hidden md:block absolute left-1/2 top-10 bottom-10 w-[1px] bg-gray-100 -translate-x-1/2"></div>
        <div className="hidden md:block">
           {renderMonth(secondMonthDate, false, true)}
        </div>
      </div>

      <div className="p-4 md:p-6 flex flex-col md:flex-row justify-between items-center border-t border-gray-100 gap-4">
        <button 
          type="button"
          onClick={() => { setStartDate(null); setEndDate(null); onSelect(''); }}
          className="w-full md:w-auto px-6 py-2.5 text-xs md:text-sm font-bold text-brand-deep/60 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
        >
          Dzēst visu
        </button>
        <button 
          type="button"
          onClick={() => {
            if (startDate) {
              const startStr = `${startDate.getDate()}. ${monthNames[startDate.getMonth()].slice(0, 3)}.`;
              const endStr = endDate ? ` - ${endDate.getDate()}. ${monthNames[endDate.getMonth()].slice(0, 3)}.` : '';
              onSelect(`${startStr}${endStr}`);
            }
            onClose();
          }}
          className="w-full md:w-auto px-10 md:px-14 py-3 md:py-4 bg-brand-accent hover:bg-brand-accentHover text-white font-black text-xs md:text-sm uppercase tracking-widest rounded-lg transition-all shadow-lg shadow-orange-100"
        >
          IZVĒLĒTIES
        </button>
      </div>
    </div>
  );
};

// --- Detail Page Components ---

const InclusionItem: React.FC<{ icon: string; title: string; text: string }> = ({ icon, title, text }) => (
  <div className="flex items-start gap-4 p-4 md:p-5 bg-brand-surface rounded-lg border border-transparent hover:border-brand-accent/20 transition-all text-left">
    <div className="w-10 h-10 md:w-12 md:h-12 bg-white rounded-md flex items-center justify-center text-brand-accent text-lg md:text-xl flex-shrink-0 shadow-sm">
      <i className={icon}></i>
    </div>
    <div>
      <h4 className="text-[9px] md:text-[11px] font-black uppercase tracking-widest text-brand-deep/40 mb-1">{title}</h4>
      <p className="text-xs md:text-sm font-black text-brand-deep leading-tight">{text}</p>
    </div>
  </div>
);

const ReviewCard: React.FC<{ name: string; date: string; rating: number; text: string }> = ({ name, date, rating, text }) => (
  <div className="bg-white p-5 md:p-6 rounded-lg border border-brand-surface shadow-sm text-left">
    <div className="flex justify-between items-start mb-4">
      <div className="flex items-center gap-3">
        <div className="w-9 h-9 md:w-10 md:h-10 rounded-lg bg-brand-surface flex items-center justify-center font-black text-brand-accent text-xs">
          {name.charAt(0)}
        </div>
        <div>
          <h4 className="font-black text-brand-deep text-[10px] md:text-[11px] uppercase tracking-widest leading-none">{name}</h4>
          <p className="text-[8px] md:text-[9px] text-brand-deep/30 font-black uppercase tracking-widest mt-1.5">{date}</p>
        </div>
      </div>
      <div className="flex items-center gap-0.5 text-[#FFD700] text-[8px] md:text-[9px]">
        {Array.from({ length: 5 }).map((_, i) => (
          <i key={i} className={`${i < rating ? 'fas' : 'far'} fa-star`}></i>
        ))}
      </div>
    </div>
    <p className="text-brand-deep/80 text-xs md:text-sm leading-relaxed font-medium italic">"{text}"</p>
  </div>
);

export const PackageDetail: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('iekļauts');
  const [isScrolled, setIsScrolled] = useState(false);
  const tabsRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  
  // Interactive Filter States
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  
  const pkg = useMemo(() => PACKAGES.find(p => p.slug === slug), [slug]);
  const countryData = useMemo(() => COUNTRIES.find(c => c.name === pkg?.country), [pkg]);
  
  // --- ROBUST INFINITE CAROUSEL LOGIC ---
  const otherTrips = useMemo(() => PACKAGES.filter(p => p.slug !== slug), [slug]);
  const [itemsPerPage, setItemsPerPage] = useState(1);
  const [carouselIndex, setCarouselIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(true);
  const carouselTimer = useRef<number | null>(null);

  // We triple the items to have enough for wrapping in both directions
  const extendedTrips = useMemo(() => [...otherTrips, ...otherTrips, ...otherTrips], [otherTrips]);

  useEffect(() => {
    const updateItemsPerPage = () => {
      const width = window.innerWidth;
      const count = width >= 1280 ? 3 : width >= 768 ? 2 : 1;
      setItemsPerPage(count);
    };
    updateItemsPerPage();
    window.addEventListener('resize', updateItemsPerPage);
    
    // Set initial index to the middle set to allow immediate left/right scrolling
    setCarouselIndex(otherTrips.length);
    
    return () => window.removeEventListener('resize', updateItemsPerPage);
  }, [otherTrips.length]);

  const startAutoplay = () => {
    stopAutoplay();
    carouselTimer.current = window.setInterval(() => {
      nextCarousel();
    }, 6000);
  };

  const stopAutoplay = () => {
    if (carouselTimer.current) {
      clearInterval(carouselTimer.current);
      carouselTimer.current = null;
    }
  };

  useEffect(() => {
    startAutoplay();
    return () => stopAutoplay();
  }, [carouselIndex]); // Reset timer on interaction

  const nextCarousel = () => {
    setIsAnimating(true);
    setCarouselIndex(prev => prev + 1);
  };
  
  const prevCarousel = () => {
    setIsAnimating(true);
    setCarouselIndex(prev => prev - 1);
  };

  const handleTransitionEnd = () => {
    // If we've reached the end of the middle set or beginning, snap back to the corresponding item in the middle set
    if (carouselIndex >= otherTrips.length * 2) {
      setIsAnimating(false);
      setCarouselIndex(carouselIndex - otherTrips.length);
    } else if (carouselIndex < otherTrips.length) {
      setIsAnimating(false);
      setCarouselIndex(carouselIndex + otherTrips.length);
    }
  };

  const [filters, setFilters] = useState({
    dates: '10.06 - 17.06',
    people: pkg?.people || 2,
    nights: pkg?.nights || 7
  });

  const checkScroll = () => {
    if (tabsRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = tabsRef.current;
      setCanScrollLeft(scrollLeft > 5);
      setCanScrollRight(scrollLeft + clientWidth < scrollWidth - 5);
    }
  };

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 400);
    };
    window.addEventListener('scroll', handleScroll);
    checkScroll();
    window.addEventListener('resize', checkScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', checkScroll);
    };
  }, []);

  if (!pkg) return null;

  const scrollToSection = (id: string) => {
    setActiveTab(id);
    const element = document.getElementById(id);
    if (element) {
      const offset = window.innerWidth < 768 ? 160 : 140;
      window.scrollTo({
        top: element.offsetTop - offset,
        behavior: 'smooth'
      });
    }
  };

  const scrollTabs = (direction: 'left' | 'right') => {
    if (tabsRef.current) {
      const scrollAmount = 200;
      tabsRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  const tabs = [
    { id: 'iekļauts', label: 'Kas iekļauts' },
    { id: 'galamerķis', label: 'Galamērķis' },
    { id: 'viesnīca', label: 'Viesnīca' },
    { id: 'numuri', label: 'Numuri' },
    { id: 'atsauksmes', label: 'Atsauksmes' }
  ];

  return (
    <div className="relative min-h-screen bg-white pb-16 md:pb-32 overflow-x-hidden">
      {/* Dynamic Background Elements */}
      <div className="fixed inset-0 z-0 pointer-events-none overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-full bg-mesh-gradient opacity-40"></div>
        <div className="absolute top-[20%] right-[-10%] w-[300px] md:w-[500px] h-[300px] md:h-[500px] bg-brand-accent/5 blob-bg rounded-full animate-float"></div>
        <div className="absolute inset-0 bg-pattern-dots opacity-[0.03]"></div>
      </div>

      <div className="relative z-10">
        {/* Breadcrumbs */}
        <div className="bg-white py-3 md:py-4 border-b border-gray-100">
          <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-20 flex items-center gap-2 md:gap-4 text-[8px] md:text-[9px] font-black uppercase tracking-widest text-brand-deep/30 overflow-x-auto no-scrollbar whitespace-nowrap">
            <Link to="/" className="hover:text-brand-accent transition-colors">Sākums</Link>
            <i className="fas fa-chevron-right text-[6px] md:text-[7px] opacity-20"></i>
            <Link to="/celojumi" className="hover:text-brand-accent transition-colors">Ceļojumi</Link>
            <i className="fas fa-chevron-right text-[6px] md:text-[7px] opacity-20"></i>
            <span className="text-brand-deep">{pkg.country}</span>
            <i className="fas fa-chevron-right text-[6px] md:text-[7px] opacity-20"></i>
            <span className="text-brand-deep/50 truncate">{pkg.hotel.name}</span>
          </div>
        </div>

        {/* Main Trip Information Bar */}
        <div className="bg-white text-brand-deep py-8 md:py-10 shadow-sm border-b border-gray-100 relative overflow-hidden">
          <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-20 relative z-10">
            <div className="flex flex-col lg:flex-row justify-between items-center lg:items-end gap-6 md:gap-8">
              <div className="animate-fade-in flex-grow text-center lg:text-left w-full">
                <h1 className="text-2xl md:text-5xl font-black mb-3 md:mb-4 tracking-tighter leading-tight text-brand-deep uppercase">
                  {pkg.hotel.name}
                </h1>
                <div className="flex flex-col md:flex-row items-center justify-center lg:justify-start gap-3 md:gap-6 mb-5 md:mb-6">
                  <div className="flex items-center gap-4">
                    <div className="flex text-[#FFD700] text-xs md:text-sm">
                      {Array.from({ length: 5 }).map((_, i) => (
                        <i key={i} className={`${i < pkg.hotel.stars ? 'fas' : 'far'} fa-star mx-0.5`}></i>
                      ))}
                    </div>
                    <div className="bg-brand-deep text-white px-2 py-0.5 rounded font-black text-[8px]">5.0/5</div>
                    <span className="text-[9px] md:text-[10px] font-bold text-brand-deep/40 tracking-wide">(167 atsauksmes)</span>
                  </div>
                </div>
                <div className="flex flex-wrap justify-center lg:justify-start items-center gap-4 md:gap-6 text-[9px] md:text-[10px] font-black uppercase tracking-widest text-brand-deep/60">
                  <div className="flex items-center gap-2">
                    <i className="fas fa-map-marker-alt text-brand-accent text-xs md:text-sm"></i>
                    <span>{pkg.city}, {pkg.country}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <i className="fas fa-plane text-brand-deep/20 text-xs md:text-sm"></i>
                    <span>Lidojums</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <i className="fas fa-home text-brand-deep/20 text-xs md:text-sm"></i>
                    <span>{pkg.categories[0]}</span>
                  </div>
                </div>
              </div>
              <div className="flex flex-col md:flex-row items-center lg:items-end gap-6 md:gap-10 text-center lg:text-right animate-fade-in w-full md:w-auto">
                <div className="space-y-1">
                  <div className="flex items-center justify-center lg:justify-end gap-2 leading-none">
                    <span className="text-[10px] md:text-xs font-bold text-brand-deep/30 uppercase">no</span>
                    <span className="text-2xl md:text-4xl font-black tracking-tighter text-brand-deep">{pkg.price}.00 €</span>
                    <span className="text-[9px] md:text-[10px] font-bold text-brand-deep/40">/pers.</span>
                  </div>
                </div>
                <button 
                  onClick={() => scrollToSection('numuri')}
                  className="w-full md:w-auto bg-brand-accent text-white px-10 py-4 md:px-12 md:py-5 rounded-lg font-black text-[10px] md:text-xs uppercase tracking-[0.2em] md:tracking-[0.3em] shadow-xl shadow-orange-100 hover:bg-brand-accentHover transition-all active:scale-95"
                >
                  IZVĒLĒTIES NUMURU
                </button>
              </div>
            </div>
          </div>
          <div className="absolute inset-0 bg-pattern-dots opacity-[0.02] pointer-events-none"></div>
        </div>

        {/* Gallery Layout */}
        <div className="max-w-[1400px] mx-auto px-4 md:px-12 lg:px-20 mt-8 md:mt-12">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3 md:gap-4 h-[250px] md:h-[600px] rounded-xl overflow-hidden shadow-xl md:shadow-2xl animate-fade-in">
            <div className="md:col-span-2 h-full">
              <img src={`https://picsum.photos/seed/${pkg.slug}-1/1200/800`} className="w-full h-full object-cover hover:scale-105 transition-transform duration-1000" alt="Hotel main" />
            </div>
            <div className="hidden md:grid grid-rows-2 gap-4 h-full">
              <img src={`https://picsum.photos/seed/${pkg.slug}-2/600/400`} className="w-full h-full object-cover hover:scale-105 transition-transform duration-1000" alt="Hotel interior" />
              <img src={`https://picsum.photos/seed/${pkg.slug}-3/600/400`} className="w-full h-full object-cover hover:scale-105 transition-transform duration-1000" alt="Hotel pool" />
            </div>
            <div className="hidden md:block h-full relative">
              <img src={`https://picsum.photos/seed/${pkg.slug}-4/600/800`} className="w-full h-full object-cover hover:scale-105 transition-transform duration-1000" alt="Hotel beach" />
              <button className="absolute bottom-10 right-10 bg-white/95 backdrop-blur-xl text-brand-deep px-10 py-5 rounded-xl font-black text-[11px] uppercase tracking-[0.3em] shadow-2xl border border-white/50 hover:bg-white hover:scale-105 transition-all">
                Visi foto (24)
              </button>
            </div>
          </div>
        </div>

        {/* Sticky Navigation - Verified Scrollable on Mobile */}
        <div className={`sticky top-20 z-40 bg-white/95 backdrop-blur-md transition-all duration-300 border-b border-gray-100 mt-8 md:mt-12 ${isScrolled ? 'shadow-xl' : ''}`}>
          <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-20 flex items-center justify-between">
            <div className="relative flex items-center group max-w-full lg:max-w-[700px]">
              {canScrollLeft && (
                <button onClick={() => scrollTabs('left')} className="absolute left-0 z-10 w-8 h-8 bg-white/90 rounded-md shadow-md flex items-center justify-center text-brand-deep border border-gray-100">
                  <i className="fas fa-chevron-left text-[9px]"></i>
                </button>
              )}
              <div ref={tabsRef} onScroll={checkScroll} className="flex items-center overflow-x-auto no-scrollbar scroll-smooth">
                {tabs.map(tab => (
                  <button
                    key={tab.id}
                    onClick={() => scrollToSection(tab.id)}
                    className={`px-5 md:px-6 py-4 md:py-5 font-black text-[9px] md:text-[10px] uppercase tracking-[0.15em] md:tracking-[0.2em] transition-all whitespace-nowrap border-b-4 ${activeTab === tab.id ? 'border-brand-accent text-brand-accent' : 'border-transparent text-brand-deep/40'}`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
              {canScrollRight && (
                <button onClick={() => scrollTabs('right')} className="absolute right-0 z-10 w-8 h-8 bg-white/90 rounded-md shadow-md flex items-center justify-center text-brand-deep border border-gray-100">
                  <i className="fas fa-chevron-right text-[9px]"></i>
                </button>
              )}
            </div>
            <div className={`hidden lg:flex items-center gap-6 transition-opacity duration-300 ${isScrolled ? 'opacity-100' : 'opacity-0'}`}>
              <div className="text-right">
                <div className="text-xl font-black text-brand-deep leading-none">€{pkg.price}</div>
                <span className="text-[8px] font-black text-brand-deep/30 uppercase tracking-widest">par personu</span>
              </div>
              <button className="bg-brand-accent text-white px-8 py-3 rounded-lg font-black text-[10px] uppercase tracking-widest shadow-lg shadow-orange-100/50 hover:bg-brand-accentHover transition-all">REZERVĒT</button>
            </div>
          </div>
        </div>

        {/* Content sections */}
        <div className="max-w-[1400px] mx-auto px-4 md:px-12 lg:px-20 mt-10 md:mt-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 md:gap-12 items-start">
            <div className="lg:col-span-8 space-y-8 md:space-y-12 pb-12 md:pb-24 text-left">
              <section id="iekļauts" className="bg-white p-6 md:p-12 rounded-lg shadow-[0_30px_100px_rgba(5,7,60,0.06)] border border-gray-100 scroll-mt-40">
                <h2 className="text-2xl md:text-4xl font-black text-brand-deep tracking-tighter mb-8 md:mb-10">Kas ir iekļauts cenā?</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InclusionItem icon="fas fa-plane" title="Lidojums" text="Rīga - Galamērķis - Rīga" />
                  <InclusionItem icon="fas fa-suitcase-rolling" title="Bagāža" text="20kg nododamā + 8kg rokas" />
                  <InclusionItem icon="fas fa-shuttle-van" title="Transfērs" text="Lidosta - Viesnīca - Lidosta" />
                  <InclusionItem icon="fas fa-hotel" title="Viesnīca" text={`${filters.nights} naktis izvēlētajā viesnīcā`} />
                  <InclusionItem icon="fas fa-utensils" title="Ēdināšana" text={pkg.mealPlan} />
                  <InclusionItem icon="fas fa-user-shield" title="Gida serviss" text="Operatīvs atbalsts galamērķī" />
                </div>
              </section>

              <section id="galamerķis" className="bg-white p-6 md:p-12 rounded-lg shadow-[0_30px_100px_rgba(5,7,60,0.06)] border border-gray-100 scroll-mt-40">
                <h2 className="text-2xl md:text-4xl font-black text-brand-deep tracking-tighter mb-8">Par galamērķi</h2>
                <div className="flex flex-col md:flex-row gap-8 md:gap-12">
                  <div className="flex-1 space-y-6">
                    <p className="text-sm md:text-base text-brand-deep/80 leading-relaxed font-medium">{countryData?.description || pkg.description}</p>
                    <div className="grid grid-cols-2 gap-4 md:gap-6 pt-2">
                      <div className="p-4 md:p-5 bg-brand-surface rounded-lg border border-gray-100">
                         <h4 className="text-[8px] md:text-[9px] font-black uppercase text-brand-deep/30 tracking-widest mb-1.5">Labākais laiks</h4>
                         <p className="text-xs md:text-sm font-black text-brand-deep">Maijs — Oktobris</p>
                      </div>
                      <div className="p-4 md:p-5 bg-brand-surface rounded-lg border border-gray-100">
                         <h4 className="text-[8px] md:text-[9px] font-black uppercase text-brand-deep/30 tracking-widest mb-1.5">Temperatūra</h4>
                         <p className="text-xs md:text-sm font-black text-brand-deep">+28°C / Jūra: +24°C</p>
                      </div>
                    </div>
                  </div>
                  <div className="flex-1">
                    <div className="h-[250px] md:h-full min-h-[250px] md:min-h-[300px] rounded-lg overflow-hidden border border-brand-surface bg-brand-surface relative ring-1 ring-black/5">
                      <img src={`https://maps.googleapis.com/maps/api/staticmap?center=${pkg.city}&zoom=11&size=600x400&key=YOUR_API_KEY`} className="absolute inset-0 w-full h-full object-cover opacity-50 grayscale" alt="Map view" />
                      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-6 h-6 md:w-8 md:h-8 bg-brand-accent rounded-md border-2 md:border-4 border-white shadow-xl animate-pulse"></div>
                    </div>
                  </div>
                </div>
              </section>

              <section id="viesnīca" className="bg-white p-6 md:p-12 rounded-lg shadow-[0_30px_100px_rgba(5,7,60,0.06)] border border-gray-100 scroll-mt-40">
                <h2 className="text-2xl md:text-3xl font-black text-brand-deep tracking-tight mb-4">Par viesnīcu</h2>
                <p className="text-xs md:text-sm text-brand-deep/60 leading-relaxed mb-8 md:mb-12">
                  Izbaudiet neaizmirstamu atpūtu {pkg.city}, {pkg.country}. Viesnīca piedāvā augstākās klases servisu un ērtības. Ideāla vieta ģimenēm un pāriem.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-16">
                  <div>
                    <h3 className="text-lg md:text-xl font-black text-brand-deep mb-6 md:mb-8 border-b border-gray-50 pb-3">Viesnīca</h3>
                    <div className="space-y-3 md:space-y-4">
                      {[
                        { icon: 'fas fa-swimming-pool', text: 'Baseini — 2' },
                        { icon: 'fas fa-glass-martini-alt', text: 'Bāri — 5' },
                        { icon: 'fas fa-utensils', text: 'A la carte restorāni — 2' },
                        { icon: 'fas fa-umbrella-beach', text: 'Atpūtas krēsli pludmalē' },
                        { icon: 'fas fa-wifi', text: 'WiFi visā teritorijā' },
                        { icon: 'fas fa-check', text: `Kategorija — ${pkg.hotel.stars}*`, highlight: true }
                      ].map((item, idx) => (
                        <div key={idx} className="flex items-center gap-3 text-xs md:text-[13px]">
                          <div className={`w-4 h-4 flex items-center justify-center flex-shrink-0 ${item.highlight ? 'text-green-500' : 'text-brand-deep/30'}`}>
                            <i className={item.icon}></i>
                          </div>
                          <span className="text-brand-deep/80 font-medium">{item.text}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div>
                    <h3 className="text-lg md:text-xl font-black text-brand-deep mb-6 md:mb-8 border-b border-gray-50 pb-3">Aktīvā atpūta</h3>
                    <div className="space-y-3 md:space-y-4">
                      {[
                        { icon: 'fas fa-door-open', text: 'Sauna' },
                        { icon: 'fas fa-check', text: 'Galda teniss', highlight: true },
                        { icon: 'fas fa-sun', text: 'Pludmales volejbols' },
                        { icon: 'fas fa-dumbbell', text: 'Trenažieru zāle' },
                        { icon: 'fas fa-spa', text: 'SPA centrs' }
                      ].map((item, idx) => (
                        <div key={idx} className="flex items-center gap-3 text-xs md:text-[13px]">
                          <div className={`w-4 h-4 flex items-center justify-center flex-shrink-0 ${item.highlight ? 'text-green-500' : 'text-brand-deep/30'}`}>
                            <i className={item.icon}></i>
                          </div>
                          <span className="text-brand-deep/80 font-medium">{item.text}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </section>

              <section id="numuri" className="bg-white p-6 md:p-12 rounded-lg shadow-[0_30px_100px_rgba(5,7,60,0.06)] border border-gray-100 scroll-mt-40">
                <h2 className="text-2xl md:text-4xl font-black text-brand-deep tracking-tighter mb-8 md:mb-10">Pieejamie numuri</h2>
                <div className="space-y-4 md:space-y-6">
                  {[
                    { name: 'Standard Room', desc: 'Mājīgs numurs ar balkonu.', features: ['20m²', 'WiFi'] },
                    { name: 'Superior Sea View', desc: 'Tiešs skats uz jūru.', features: ['28m²', 'WiFi', 'Mini-bārs'] }
                  ].map((room, idx) => (
                    <div key={idx} className="flex flex-col md:flex-row gap-5 md:gap-6 p-4 md:p-6 bg-brand-surface/50 rounded-lg border border-gray-100 hover:bg-white transition-all">
                      <div className="w-full md:w-48 h-32 md:h-36 rounded-lg overflow-hidden flex-shrink-0 shadow-sm">
                        <img src={`https://picsum.photos/seed/${room.name}/600/400`} className="w-full h-full object-cover" alt={room.name} />
                      </div>
                      <div className="flex-grow text-left">
                         <h4 className="text-lg md:text-xl font-black text-brand-deep mb-1 md:mb-2">{room.name}</h4>
                         <p className="text-xs md:text-sm text-brand-deep/60 font-medium mb-4">{room.desc}</p>
                         <div className="flex flex-wrap gap-2">
                           {room.features.map(f => <span key={f} className="bg-white px-2.5 py-1 rounded-md text-[9px] font-bold text-brand-deep/40 border border-gray-100">{f}</span>)}
                         </div>
                      </div>
                      <div className="flex flex-row md:flex-col justify-between items-center md:items-end gap-3 pt-4 md:pt-0 border-t md:border-t-0 border-gray-100">
                         <div className="text-green-500 text-[9px] font-black uppercase tracking-widest flex items-center gap-1.5">
                           <i className="fas fa-bolt"></i> Pieejams
                         </div>
                         <button className="bg-brand-deep text-white px-6 md:px-8 py-2 md:py-3 rounded-lg font-black text-[9px] uppercase tracking-widest hover:bg-brand-accent transition-colors active:scale-95">Izvēlēties</button>
                      </div>
                    </div>
                  ))}
                </div>
              </section>

              <section id="atsauksmes" className="bg-white p-6 md:p-12 rounded-lg shadow-[0_30px_100px_rgba(5,7,60,0.06)] border border-gray-100 scroll-mt-40">
                <div className="flex flex-col md:flex-row justify-between items-start md:items-end mb-8 md:mb-12 gap-6">
                  <div className="text-left">
                    <h2 className="text-2xl md:text-4xl font-black text-brand-deep tracking-tighter">Viesu atsauksmes</h2>
                    <p className="text-[10px] font-bold uppercase tracking-widest mt-2 text-brand-deep/30">Balstoties uz 124 vērtējumiem</p>
                  </div>
                  <div className="flex items-center gap-4 md:gap-6 bg-brand-surface px-6 md:px-8 py-3 md:py-4 rounded-xl border border-gray-100 w-full md:w-auto">
                     <div className="text-center flex-shrink-0">
                       <div className="text-2xl md:text-3xl font-black text-brand-deep leading-none">4.7</div>
                       <div className="text-[8px] md:text-[9px] font-black text-brand-deep/30 uppercase tracking-widest mt-1">Vērtējums</div>
                     </div>
                     <div className="w-[1px] h-8 bg-gray-200"></div>
                     <div className="text-brand-accent flex text-xs">
                       {[1,2,3,4,5].map(s => <i key={s} className="fas fa-star"></i>)}
                     </div>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <ReviewCard name="Līga Bērziņa" date="2025. gada Jūnijs" rating={5} text="Lieliska atpūta! Viesnīca pārsniedza visas cerības, personāls bija ļoti atsaucīgs." />
                  <ReviewCard name="Jānis Ozols" date="2025. gada Maijs" rating={4} text="Labs serviss un skaista atrašanās vieta. Noteikti iesaku ģimenēm." />
                </div>
              </section>
            </div>

            <aside className="lg:col-span-4 lg:sticky lg:top-40 space-y-6 w-full">
              {/* Inquiry Widget - Optimized spacing */}
              <div className="bg-white/95 backdrop-blur-xl rounded-lg p-6 md:p-8 shadow-xl border border-white/40 ring-1 ring-black/5 relative z-[30] text-left">
                <div className="flex justify-between items-end mb-8">
                  <div>
                    <span className="text-[9px] md:text-[10px] font-black text-brand-deep/30 uppercase tracking-widest block mb-1">Cena no</span>
                    <div className="text-3xl md:text-4xl font-black text-brand-deep leading-none tracking-tighter">€{pkg.price}</div>
                  </div>
                  <div className="text-right">
                    <div className="bg-green-50 text-green-600 px-2 py-0.5 rounded-md text-[8px] font-black uppercase tracking-widest mb-1 border border-green-100">Pieejams</div>
                    <span className="text-[8px] font-bold text-brand-deep/40">Visas nodokļi iekļauti</span>
                  </div>
                </div>
                <div className="space-y-4 md:space-y-6 mb-8">
                  <div className="space-y-2 relative">
                    <label className="text-[9px] md:text-[10px] font-black text-brand-deep/40 uppercase tracking-widest ml-1">Lidojuma datumi</label>
                    <div onClick={() => setActiveDropdown(activeDropdown === 'dates' ? null : 'dates')} className="p-3.5 md:p-4 bg-brand-surface rounded-lg border border-gray-100 hover:bg-white transition-all flex items-center justify-between cursor-pointer">
                      <div className="flex items-center gap-3">
                        <i className="far fa-calendar text-brand-deep/20 text-xs"></i>
                        <span className="text-xs md:text-sm font-black text-brand-deep">{filters.dates}</span>
                      </div>
                      <i className={`fas fa-chevron-down text-[9px] text-brand-deep/20 transition-transform ${activeDropdown === 'dates' ? 'rotate-180' : ''}`}></i>
                    </div>
                    <DropdownWrapper position="bottom" isOpen={activeDropdown === 'dates'} onClose={() => setActiveDropdown(null)} className="w-full">
                      <CalendarPicker onSelect={(val) => setFilters({...filters, dates: val})} onClose={() => setActiveDropdown(null)} />
                    </DropdownWrapper>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2 relative">
                      <label className="text-[9px] md:text-[10px] font-black text-brand-deep/40 uppercase tracking-widest ml-1">Ceļotāji</label>
                      <div onClick={() => setActiveDropdown(activeDropdown === 'people' ? null : 'people')} className="p-3.5 md:p-4 bg-brand-surface rounded-lg border border-gray-100 flex items-center justify-between cursor-pointer">
                        <div className="flex items-center gap-2">
                          <i className="fas fa-user-friends text-brand-deep/20 text-xs"></i>
                          <span className="text-xs md:text-sm font-black text-brand-deep">{filters.people}</span>
                        </div>
                        <i className={`fas fa-chevron-down text-[9px] text-brand-deep/20 transition-transform ${activeDropdown === 'people' ? 'rotate-180' : ''}`}></i>
                      </div>
                      <DropdownWrapper position="bottom" isOpen={activeDropdown === 'people'} onClose={() => setActiveDropdown(null)} className="w-full">
                        <div className="p-3 space-y-1">
                          {[1, 2, 3, 4, 5, 6].map(n => (
                            <div key={n} onClick={() => { setFilters({...filters, people: n}); setActiveDropdown(null); }} className={`px-4 py-3 text-xs md:text-sm font-black rounded-lg cursor-pointer transition-all ${filters.people === n ? 'bg-brand-accent text-white shadow-md' : 'text-brand-deep hover:bg-brand-surface'}`}>
                              {n} {n === 1 ? 'persona' : 'personas'}
                            </div>
                          ))}
                        </div>
                      </DropdownWrapper>
                    </div>
                    <div className="space-y-2 relative">
                      <label className="text-[9px] md:text-[10px] font-black text-brand-deep/40 uppercase tracking-widest ml-1">Ilgums</label>
                      <div onClick={() => setActiveDropdown(activeDropdown === 'nights' ? null : 'nights')} className="p-3.5 md:p-4 bg-brand-surface rounded-lg border border-gray-100 flex items-center justify-between cursor-pointer">
                        <div className="flex items-center gap-2">
                          <i className="far fa-moon text-brand-deep/20 text-xs"></i>
                          <span className="text-xs md:text-sm font-black text-brand-deep">{filters.nights} n.</span>
                        </div>
                        <i className={`fas fa-chevron-down text-[9px] text-brand-deep/20 transition-transform ${activeDropdown === 'nights' ? 'rotate-180' : ''}`}></i>
                      </div>
                      <DropdownWrapper position="bottom" isOpen={activeDropdown === 'nights'} onClose={() => setActiveDropdown(null)} className="w-full">
                        <div className="p-3 space-y-1">
                          {[3, 5, 7, 10, 14, 21].map(n => (
                            <div key={n} onClick={() => { setFilters({...filters, nights: n}); setActiveDropdown(null); }} className={`px-4 py-3 text-xs md:text-sm font-black rounded-lg cursor-pointer transition-all ${filters.nights === n ? 'bg-brand-accent text-white shadow-md' : 'text-brand-deep hover:bg-brand-surface'}`}>
                              {n} naktis
                            </div>
                          ))}
                        </div>
                      </DropdownWrapper>
                    </div>
                  </div>
                </div>
                <button onClick={() => navigate('/kontakti')} className="w-full bg-brand-accent text-white py-5 md:py-6 rounded-lg font-black text-[11px] md:text-sm uppercase tracking-[0.2em] shadow-xl shadow-orange-100 hover:bg-brand-accentHover transition-all flex items-center justify-center gap-4 active:scale-95">
                  PIETEIKT CEĻOJUMU <i className="fas fa-arrow-right"></i>
                </button>
              </div>

              {/* Contact Widget */}
              <div className="bg-brand-deep rounded-lg p-6 md:p-8 text-white relative overflow-hidden group cursor-pointer shadow-xl text-left" onClick={() => navigate('/kontakti')}>
                <div className="absolute top-0 right-0 w-32 h-32 bg-brand-accent/20 rounded-lg blur-3xl"></div>
                <h4 className="text-lg md:text-xl font-black mb-3 relative z-10 text-white leading-tight">Jautājumi par šo piedāvāvumu?</h4>
                <p className="text-white/60 text-[10px] md:text-xs font-bold leading-relaxed mb-6 relative z-10">Zvaniet mums vai rakstiet čatā — mēs palīdzēsim!</p>
                <div className="flex items-center gap-4 relative z-10">
                  <div className="w-9 h-9 md:w-10 md:h-10 bg-brand-accent rounded-lg flex items-center justify-center text-white shadow-lg"><i className="fas fa-phone-alt text-sm"></i></div>
                  <span className="font-black text-base md:text-lg tracking-tight text-white hover:text-brand-accent transition-colors">+371 20000000</span>
                </div>
              </div>
            </aside>
          </div>
        </div>

        {/* --- INFINITE TRIP CAROUSEL SECTION --- */}
        <section className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-20 mt-20 md:mt-32 relative">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-end mb-10 md:mb-16 gap-6 md:gap-8 text-left">
            <div className="space-y-3 md:space-y-4">
               <div className="inline-block bg-brand-accent text-white px-4 py-1.5 rounded-full text-[9px] font-black uppercase tracking-[0.2em] shadow-lg">Citi piedāvājumi</div>
               <h2 className="text-3xl md:text-5xl font-black text-brand-deep tracking-tighter leading-tight">
                 Iedvesmojies nākamajam <br className="hidden md:block" /> <span className="text-brand-accent italic">piedzīvojumam</span>
               </h2>
            </div>
            <div className="flex items-center gap-3">
              <button onClick={() => { stopAutoplay(); prevCarousel(); }} className="w-12 h-12 md:w-14 md:h-14 bg-white border border-gray-100 rounded-lg shadow-xl flex items-center justify-center text-brand-deep hover:text-brand-accent transition-all active:scale-95 group">
                <i className="fas fa-chevron-left group-hover:-translate-x-1 transition-transform"></i>
              </button>
              <button onClick={() => { stopAutoplay(); nextCarousel(); }} className="w-12 h-12 md:w-14 md:h-14 bg-white border border-gray-100 rounded-lg shadow-xl flex items-center justify-center text-brand-deep hover:text-brand-accent transition-all active:scale-95 group">
                <i className="fas fa-chevron-right group-hover:translate-x-1 transition-transform"></i>
              </button>
            </div>
          </div>

          <div 
            className="relative overflow-hidden md:overflow-visible"
            onMouseEnter={stopAutoplay}
            onMouseLeave={startAutoplay}
          >
            <div 
              className={`flex gap-4 md:gap-8 ${isAnimating ? 'transition-transform duration-700 ease-in-out' : 'transition-none'}`} 
              onTransitionEnd={handleTransitionEnd}
              style={{ transform: `translateX(-${carouselIndex * (100 / itemsPerPage)}%)` }}
            >
              {extendedTrips.map((p, idx) => (
                <div key={`${p.id}-${idx}`} className="min-w-full md:min-w-[calc(50%-16px)] xl:min-w-[calc(33.333%-21.333px)]">
                  <PackageCard pkg={p} />
                </div>
              ))}
            </div>
          </div>
          
          <div className="mt-12 md:mt-16 text-center">
            <button onClick={() => navigate('/celojumi')} className="w-full md:w-auto group border-2 border-brand-deep rounded-lg px-10 py-4 md:px-12 md:py-5 inline-flex items-center justify-center gap-4 hover:bg-brand-deep hover:text-white transition-all shadow-xl shadow-brand-deep/5">
              <span className="text-[10px] md:text-xs font-black uppercase tracking-[0.2em]">Skatīt visus ceļojumus</span>
              <i className="fas fa-arrow-right text-brand-accent group-hover:translate-x-2 transition-transform"></i>
            </button>
          </div>
        </section>
      </div>
    </div>
  );
};
